import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, FileDown, History, Plus, Repeat } from 'lucide-react';
import { LedgerEntrySummary, useAccessApi } from '../../../services/api';
import {
    useAdminCompanyTeams,
    usePricingConfigs,
    useSow,
    useSowLedger,
    useSowPricingConfig,
    useSows,
} from '../../../hooks/useApi';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import { toPoolBalanceRows } from './adminCreditAdapters';
import { formatInclusiveDate, formatInclusiveDateRange } from './dateDisplay';
import { clampUsageProgressPercent } from './sowUsageProgress';
import { SowPoolUsageSection } from './SowUsageDisplay.tsx';
import { sortPoolTypesForDisplay } from './sowLedgerTableGrouping';
import { SowLedgerTableSection } from './SowLedgerTableSection';
import { EditSowDialog } from './sowDialogs';
import {
    LedgerEventDialog,
    ManualLedgerEntryDialog,
    ReassignLedgerDialog,
    RolloverDialog,
    SowAuditDialog,
} from './ledgerDialogs';
import { AgreementStatusBadge } from './AgreementStatusBadge';
import { SowWriteAccessSection } from './SowWriteAccessSection';
import { DialogFrame } from './creditDialogPrimitives';
import type { AdminFromCompanyDetailState } from '../../Breadcrumbs';
import { useAdminBreadcrumbBack } from '../../../hooks/useAdminBreadcrumbBack';
import { PulseSeatAssignmentDialog, PulseSeatQuantityDialog } from './PulseSeatAssignmentSection';
import { summarizePulseSeatInventory } from './pulseSeatStatus';
import { SowUsageCsvImportDialog } from './SowUsageCsvImportDialog';
import { CreditCopyButton } from './CreditCopyButton';

type LedgerModal =
    | null
    | { type: 'grant'; defaultPoolType?: string }
    | { type: 'adjustment_row'; entry: LedgerEntrySummary }
    | { type: 'refund'; entry: LedgerEntrySummary }
    | { type: 'reassign'; entry: LedgerEntrySummary };

export function SowCreditDetail() {
    const { sowId } = useParams<{ sowId: string }>();
    const { goBack } = useAdminBreadcrumbBack();
    const { exportSowLedgerCsv } = useAccessApi();
    const entityLinkClass =
        'rounded-sm font-medium text-[#276db0] no-underline outline-none hover:text-[#215892] focus-visible:ring-2 focus-visible:ring-[#276db0]/35';
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [ledgerModal, setLedgerModal] = useState<LedgerModal>(null);
    const [isManualLedgerOpen, setIsManualLedgerOpen] = useState(false);
    const [isRolloverOpen, setIsRolloverOpen] = useState(false);
    const [rolloverInitialPoolType, setRolloverInitialPoolType] = useState<string | undefined>();
    const [isPulseSeatsOpen, setIsPulseSeatsOpen] = useState(false);
    const [isPulseSeatQuantityOpen, setIsPulseSeatQuantityOpen] = useState(false);
    const [isLedgerExporting, setIsLedgerExporting] = useState(false);
    const [isSowAuditOpen, setIsSowAuditOpen] = useState(false);
    const [isUsageCsvImportOpen, setIsUsageCsvImportOpen] = useState(false);
    const [usageCsvImportMessage, setUsageCsvImportMessage] = useState<string | null>(null);
    const sowQuery = useSow(sowId ?? null);
    const needsAllSows = ledgerModal?.type === 'reassign' || isRolloverOpen;
    const sowsQuery = useSows({}, needsAllSows);
    const ledgerQuery = useSowLedger(sowId ?? null);
    const sowPricingConfigQuery = useSowPricingConfig(
        sowId ?? null,
        Boolean(sowQuery.data?.sow?.pricingConfigId),
    );
    const pricingCatalogQuery = usePricingConfigs(true, isEditOpen);
    const teamsQuery = useAdminCompanyTeams(
        isEditOpen ? (sowQuery.data?.sow?.companyId ?? null) : null,
    );

    const sow = sowQuery.data?.sow;
    const pulseSub =
        sow?.PulseSubscriptions?.find((p) => p.sowId === sow.id) ?? sow?.PulseSubscriptions?.[0];
    const hasPulseSubscription = (sow?.PulseSubscriptions?.length ?? 0) > 0;
    const pulseSeatInventory = useMemo(
        () => summarizePulseSeatInventory(pulseSub?.PulseSeats ?? []),
        [pulseSub?.PulseSeats],
    );
    const balance = sow?.balanceSummary ?? undefined;
    const poolRows = useMemo(() => toPoolBalanceRows(balance), [balance]);
    const ledgerEntries = useMemo(
        () => ledgerQuery.data?.ledgerEntries ?? [],
        [ledgerQuery.data?.ledgerEntries],
    );
    const ledgerPoolTypes = useMemo(() => {
        const types = new Set<string>();
        for (const pool of sow?.CreditPools ?? []) {
            types.add(pool.poolType);
        }
        for (const entry of ledgerEntries) {
            types.add(entry.poolType);
        }
        return sortPoolTypesForDisplay(types);
    }, [ledgerEntries, sow?.CreditPools]);
    const pricingConfig = sowPricingConfigQuery.data?.pricingConfig;
    const companyDisplayName =
        sow?.Company?.name?.trim() || (sow ? `Company ${sow.companyId}` : '—');
    const sowTeamLinks = useMemo(() => {
        const entries = sow?.SowTeams ?? [];
        return [...entries]
            .sort((a, b) => a.teamId - b.teamId)
            .map((st) => ({
                teamId: st.teamId,
                label: st.Team?.name?.trim() || `Team ${st.teamId}`,
            }));
    }, [sow?.SowTeams]);

    const canManagePulseSeats = sow?.state === 'active';

    useEffect(() => {
        if (!canManagePulseSeats && isPulseSeatsOpen) {
            setIsPulseSeatsOpen(false);
        }
        if (!canManagePulseSeats && isPulseSeatQuantityOpen) {
            setIsPulseSeatQuantityOpen(false);
        }
    }, [canManagePulseSeats, isPulseSeatQuantityOpen, isPulseSeatsOpen]);

    if (sowQuery.isLoading) {
        return (
            <Card>
                <CardContent className="p-8 text-sm text-neutral-500">Loading SOW...</CardContent>
            </Card>
        );
    }

    if (!sow) {
        return (
            <Card>
                <CardContent className="p-8 text-sm text-neutral-600">SOW not found.</CardContent>
            </Card>
        );
    }

    const ledgerDialogMode =
        ledgerModal?.type === 'grant'
            ? 'grant'
            : ledgerModal?.type === 'refund'
              ? 'refund'
              : ledgerModal?.type === 'adjustment_row'
                ? 'adjustment'
                : 'grant';

    const ledgerSourceEntry =
        ledgerModal?.type === 'adjustment_row' || ledgerModal?.type === 'refund'
            ? ledgerModal.entry
            : undefined;
    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                <div className="flex items-start gap-3">
                    <Button variant="ghost" size="icon" onClick={goBack}>
                        <ArrowLeft className="size-5" />
                    </Button>
                    <div>
                        <p className="text-sm text-neutral-500">SOW</p>
                        <h1 className="text-3xl font-semibold text-neutral-950">{sow.name}</h1>
                        <div className="mt-1 flex items-center gap-1.5 text-sm text-neutral-500">
                            <span>ID: {sow.id}</span>
                            <CreditCopyButton value={sow.id} label="Copy SOW ID" />
                        </div>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button
                        variant="outline"
                        className="gap-2"
                        onClick={() => setIsSowAuditOpen(true)}
                    >
                        <History className="size-4" />
                        Audit history
                    </Button>
                    <Button onClick={() => setIsEditOpen(true)}>Edit</Button>
                </div>
            </div>

            <Card>
                <CardContent className="grid gap-6 p-6">
                    <div className="grid gap-6 border-b border-neutral-100 pb-6 md:grid-cols-3">
                        <div>
                            <p className="text-sm text-neutral-500">Company</p>
                            <p className="mt-1 flex flex-wrap items-baseline gap-x-1 gap-y-0.5">
                                <Link
                                    to={`/admin/companies/${sow.companyId}`}
                                    className={entityLinkClass}
                                >
                                    {companyDisplayName}
                                </Link>
                                <span className="text-sm tabular-nums text-neutral-500">
                                    (#{sow.companyId})
                                </span>
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-neutral-500">Teams on this SOW</p>
                            {sowTeamLinks.length === 0 ? (
                                <p className="mt-1 text-sm font-medium text-neutral-950">
                                    None linked
                                </p>
                            ) : (
                                <ul className="mt-2 list-none space-y-2">
                                    {sowTeamLinks.map(({ teamId, label }) => (
                                        <li key={teamId}>
                                            <span className="flex flex-wrap items-baseline gap-x-1 gap-y-0.5 text-sm">
                                                <Link
                                                    to={`/admin/companies/${sow.companyId}?tab=teams&expandTeam=${encodeURIComponent(String(teamId))}`}
                                                    className={entityLinkClass}
                                                >
                                                    {label}
                                                </Link>
                                                <span className="tabular-nums text-neutral-500">
                                                    (#{teamId})
                                                </span>
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div>
                            <p className="text-sm text-neutral-500">Status</p>
                            <AgreementStatusBadge className="mt-1" status={sow.state} />
                        </div>
                    </div>
                    <div className="grid gap-6 md:grid-cols-4">
                        <MetaItem
                            label="Contract Period"
                            value={
                                formatInclusiveDateRange(sow.startDate, sow.endDate) ??
                                `${formatInclusiveDate(sow.startDate)} - Open`
                            }
                            hint="End date is inclusive."
                        />
                        <MetaItem label="Unit Mode" value={sow.unitMode} />
                        <div>
                            <p className="text-sm text-neutral-500">Pricing Configuration</p>
                            {sow.pricingConfigId ? (
                                <Link
                                    to={`/admin/pricing-configs/${sow.pricingConfigId}`}
                                    state={
                                        {
                                            fromSowDetail: {
                                                sowId: sow.id,
                                                sowName: sow.name,
                                                companyId: String(sow.companyId),
                                                companyLabel: companyDisplayName,
                                            },
                                        } satisfies AdminFromCompanyDetailState
                                    }
                                    className={`mt-1 inline-block ${entityLinkClass}`}
                                >
                                    {pricingConfig
                                        ? `${pricingConfig.name} v${pricingConfig.version}`
                                        : sow.pricingConfigId}
                                </Link>
                            ) : (
                                <p className="mt-1 font-medium text-neutral-950">—</p>
                            )}
                        </div>
                        <SowWriteAccessSection sow={sow} />
                    </div>
                </CardContent>
            </Card>

            <section className="grid gap-4 md:grid-cols-2">
                {poolRows.length === 0 ? (
                    <Card>
                        <CardContent className="space-y-4 p-6">
                            <p className="text-sm text-neutral-500">
                                No balance pools are available for this SOW yet.
                            </p>
                            <div className="flex flex-wrap gap-2">
                                <Button
                                    variant="outline"
                                    className="w-full sm:w-auto"
                                    onClick={() => {
                                        setRolloverInitialPoolType(undefined);
                                        setIsRolloverOpen(true);
                                    }}
                                >
                                    <Repeat className="size-4" />
                                    Rollover
                                </Button>
                                <Button
                                    variant="outline"
                                    className="w-full sm:w-auto"
                                    onClick={() => setLedgerModal({ type: 'grant' })}
                                >
                                    + Grant Credits
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                ) : (
                    poolRows.map((pool) => (
                        <Card key={pool.poolType}>
                            <CardContent className="space-y-4 p-6">
                                <SowPoolUsageSection
                                    pool={pool}
                                    footer={
                                        pool.poolType === 'pulse_credit' && pulseSub ? (
                                            <p className="text-sm text-neutral-500">
                                                Subscription ID: {pulseSub.id}
                                            </p>
                                        ) : undefined
                                    }
                                />
                                {pool.resetPolicy?.type === 'periodic_release' &&
                                pool.periodSummaries.length > 0 ? (
                                    <PoolPeriodHistorySection pool={pool} />
                                ) : null}
                                <div className="flex flex-wrap gap-2">
                                    <Button
                                        variant="outline"
                                        className="w-full sm:w-auto"
                                        onClick={() => {
                                            setRolloverInitialPoolType(pool.poolType);
                                            setIsRolloverOpen(true);
                                        }}
                                    >
                                        <Repeat className="size-4" />
                                        Rollover
                                    </Button>
                                    <Button
                                        variant="outline"
                                        className="w-full sm:w-auto"
                                        onClick={() =>
                                            setLedgerModal({
                                                type: 'grant',
                                                defaultPoolType: pool.poolType,
                                            })
                                        }
                                    >
                                        + Grant Credits
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    ))
                )}
                {hasPulseSubscription && pulseSub ? (
                    <Card>
                        <CardContent className="space-y-4 p-6">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <p className="text-sm font-semibold text-neutral-950">
                                        Pulse seats
                                    </p>
                                    <p className="mt-1 text-2xl font-semibold tabular-nums text-neutral-950">
                                        {pulseSeatInventory.assignedCount} /{' '}
                                        {pulseSeatInventory.currentPurchasedCount}
                                    </p>
                                    <p className="text-sm text-neutral-500">assigned / purchased</p>
                                    {pulseSeatInventory.pendingDowngradeCount > 0 ? (
                                        <p className="mt-1 text-sm text-amber-800">
                                            → {pulseSeatInventory.futurePurchasedCount} after
                                            pending downgrade
                                        </p>
                                    ) : null}
                                </div>
                                <div className="text-right">
                                    <p className="text-sm text-neutral-500">Available</p>
                                    <p className="text-lg font-semibold tabular-nums text-neutral-950">
                                        {pulseSeatInventory.availableCount}
                                    </p>
                                </div>
                            </div>
                            <div className="h-2 overflow-hidden rounded-full bg-neutral-100">
                                <div
                                    className="h-full rounded-full bg-[#276db0]"
                                    style={{
                                        width: `${
                                            pulseSeatInventory.currentPurchasedCount > 0
                                                ? clampUsageProgressPercent(
                                                      Math.round(
                                                          (pulseSeatInventory.assignedCount /
                                                              pulseSeatInventory.currentPurchasedCount) *
                                                              100,
                                                      ),
                                                  )
                                                : 0
                                        }%`,
                                    }}
                                />
                            </div>
                            <p className="text-sm text-neutral-500">
                                {pulseSeatInventory.currentPurchasedCount > 0
                                    ? `${Math.round((pulseSeatInventory.assignedCount / pulseSeatInventory.currentPurchasedCount) * 100)}% of seats assigned`
                                    : 'No seats purchased'}
                            </p>
                            {pulseSeatInventory.pendingDowngradeCount > 0 ? (
                                <p className="text-sm text-amber-800">
                                    {pulseSeatInventory.pendingDowngradeCount} seat
                                    {pulseSeatInventory.pendingDowngradeCount === 1 ? '' : 's'}{' '}
                                    pending downgrade
                                    {pulseSeatInventory.pendingDowngradeExpirationLabels.length > 0
                                        ? ` until ${pulseSeatInventory.pendingDowngradeExpirationLabels.join(', ')}`
                                        : ''}
                                    . Seats stay available until then; expired seats reactivate
                                    first when quantity is increased.
                                </p>
                            ) : null}
                            <div className="flex flex-wrap gap-2">
                                <Button
                                    variant="outline"
                                    className="w-full sm:w-auto"
                                    disabled={!canManagePulseSeats}
                                    title={
                                        canManagePulseSeats
                                            ? undefined
                                            : 'Available only while this SOW is active (contract period started).'
                                    }
                                    onClick={() => setIsPulseSeatsOpen(true)}
                                >
                                    Assign/Unassign
                                </Button>
                                <Button
                                    variant="outline"
                                    className="w-full sm:w-auto"
                                    disabled={!canManagePulseSeats}
                                    title={
                                        canManagePulseSeats
                                            ? undefined
                                            : 'Available only while this SOW is active (contract period started).'
                                    }
                                    onClick={() => setIsPulseSeatQuantityOpen(true)}
                                >
                                    Change quantity
                                </Button>
                                {!canManagePulseSeats ? (
                                    <p className="text-sm text-neutral-500">
                                        Pulse seat assignments are available if the SOW is active.
                                    </p>
                                ) : null}
                            </div>
                        </CardContent>
                    </Card>
                ) : null}
            </section>

            <section className="space-y-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <h2 className="text-xl font-semibold text-neutral-950">Usage</h2>
                    <div className="flex flex-wrap gap-2 sm:justify-end">
                        <Button
                            variant="outline"
                            size="sm"
                            className="gap-2"
                            disabled={isLedgerExporting}
                            onClick={async () => {
                                setIsLedgerExporting(true);
                                try {
                                    await exportSowLedgerCsv(sow.id, sow.name);
                                } catch (e) {
                                    console.error(e);
                                    window.alert(
                                        e instanceof Error
                                            ? e.message
                                            : 'Could not export ledger CSV',
                                    );
                                } finally {
                                    setIsLedgerExporting(false);
                                }
                            }}
                        >
                            <FileDown className="size-4" />
                            {isLedgerExporting ? 'Exporting…' : 'Export CSV'}
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            className="gap-2"
                            onClick={() => {
                                setUsageCsvImportMessage(null);
                                setIsUsageCsvImportOpen(true);
                            }}
                        >
                            Import CSV
                        </Button>
                        <Button
                            size="sm"
                            className="gap-2 bg-neutral-950 text-white shadow-sm hover:bg-neutral-900"
                            onClick={() => setIsManualLedgerOpen(true)}
                        >
                            <Plus className="size-4" aria-hidden />
                            Manual usage entry
                        </Button>
                    </div>
                </div>
                {usageCsvImportMessage ? (
                    <p
                        className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
                        role="status"
                    >
                        {usageCsvImportMessage}
                    </p>
                ) : null}
                <SowLedgerTableSection
                    ledgerEntries={ledgerEntries}
                    poolTypes={ledgerPoolTypes}
                    onAdjustRow={(entry) =>
                        setLedgerModal({
                            type: 'adjustment_row',
                            entry,
                        })
                    }
                    onRefundRow={(entry) => setLedgerModal({ type: 'refund', entry })}
                    onReassignRow={(entry) => setLedgerModal({ type: 'reassign', entry })}
                />
            </section>

            <EditSowDialog
                open={isEditOpen}
                onClose={() => setIsEditOpen(false)}
                sow={sow}
                pricingConfigs={pricingCatalogQuery.data?.pricingConfigs ?? []}
                teams={(teamsQuery.data?.teams ?? [])
                    .filter((team) => team.isActive !== false)
                    .map((team) => ({ id: Number(team.id), name: team.name }))
                    .filter((team) => Number.isFinite(team.id))}
                teamsLoading={teamsQuery.isPending}
            />
            <LedgerEventDialog
                mode={ledgerDialogMode}
                open={ledgerModal !== null && ledgerModal.type !== 'reassign'}
                onClose={() => setLedgerModal(null)}
                sowId={sow.id}
                pools={sow.CreditPools ?? []}
                ledgerEntries={ledgerEntries}
                sourceLedgerEntry={ledgerSourceEntry}
                grantDefaultPoolType={
                    ledgerModal?.type === 'grant' ? ledgerModal.defaultPoolType : undefined
                }
            />
            <ManualLedgerEntryDialog
                open={isManualLedgerOpen}
                onClose={() => setIsManualLedgerOpen(false)}
                sowId={sow.id}
                pools={sow.CreditPools ?? []}
                teams={sowTeamLinks}
            />
            <SowUsageCsvImportDialog
                open={isUsageCsvImportOpen}
                onClose={() => setIsUsageCsvImportOpen(false)}
                sowId={sow.id}
                sowName={sow.name}
                pools={sow.CreditPools ?? []}
                teams={sowTeamLinks}
                onApplied={(appliedRowCount) => {
                    setUsageCsvImportMessage(
                        `Imported ${appliedRowCount} usage ${
                            appliedRowCount === 1 ? 'event' : 'events'
                        }.`,
                    );
                }}
            />
            {ledgerModal?.type === 'reassign' ? (
                <ReassignLedgerDialog
                    open
                    onClose={() => setLedgerModal(null)}
                    sow={sow}
                    allSows={sowsQuery.data?.sows ?? []}
                    sourceLedgerEntry={ledgerModal.entry}
                    ledgerEntries={ledgerEntries}
                />
            ) : null}
            <RolloverDialog
                open={isRolloverOpen}
                onClose={() => {
                    setIsRolloverOpen(false);
                    setRolloverInitialPoolType(undefined);
                }}
                sow={sow}
                allSows={sowsQuery.data?.sows ?? []}
                initialPoolType={rolloverInitialPoolType}
            />
            {isPulseSeatsOpen ? (
                <PulseSeatAssignmentDialog
                    open
                    onClose={() => setIsPulseSeatsOpen(false)}
                    sow={sow}
                />
            ) : null}
            {isPulseSeatQuantityOpen ? (
                <PulseSeatQuantityDialog
                    open
                    onClose={() => setIsPulseSeatQuantityOpen(false)}
                    sow={sow}
                    pulseSeatInventory={pulseSeatInventory}
                />
            ) : null}
            <SowAuditDialog
                open={isSowAuditOpen}
                onClose={() => setIsSowAuditOpen(false)}
                sowId={sow.id}
            />
        </div>
    );
}

function PoolPeriodHistorySection({
    pool,
}: {
    pool: ReturnType<typeof toPoolBalanceRows>[number];
}) {
    const [open, setOpen] = useState(false);
    const unusedBehavior =
        pool.resetPolicy?.type === 'periodic_release' ? pool.resetPolicy.unusedBehavior : null;
    const unusedCreditsColumn =
        unusedBehavior === 'carry_forward'
            ? {
                  label: 'Carry',
                  value: (period: (typeof pool.periodSummaries)[number]) =>
                      Number(period.carriedIntoNext),
              }
            : {
                  label: 'Expired',
                  value: (period: (typeof pool.periodSummaries)[number]) => Number(period.expired),
              };

    return (
        <>
            <button
                type="button"
                className="rounded-sm text-sm font-medium text-[#276db0] outline-none hover:text-[#215892] focus-visible:ring-2 focus-visible:ring-[#276db0]/35"
                onClick={() => setOpen(true)}
            >
                Usage history by period
            </button>
            <DialogFrame
                open={open}
                title="Usage history by period"
                description="Review how released credits were used in each inclusive period."
                onClose={() => setOpen(false)}
                panelClassName="max-w-3xl"
            >
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead>
                            <tr className="border-b border-neutral-200 text-left text-neutral-500">
                                <th className="pb-2 pr-4 font-medium">Period (inclusive)</th>
                                <th className="pb-2 pr-4 font-medium">Released</th>
                                <th className="pb-2 pr-4 font-medium">Used</th>
                                <th className="pb-2 font-medium">{unusedCreditsColumn.label}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pool.periodSummaries.map((period) => {
                                const used = Math.max(
                                    Number(period.usageCommitted) + Number(period.usagePending),
                                    0,
                                );
                                return (
                                    <tr
                                        key={`${pool.poolType}-${period.index}`}
                                        className="border-b border-neutral-100 last:border-b-0"
                                    >
                                        <td className="py-2 pr-4">
                                            <div className="font-medium text-neutral-950">
                                                {formatInclusiveDateRange(
                                                    period.startDate,
                                                    period.endDate,
                                                )}
                                            </div>
                                            <div className="text-xs text-neutral-500">
                                                {period.isCurrent ? 'Current period' : 'Completed'}
                                            </div>
                                        </td>
                                        <td className="py-2 pr-4 tabular-nums">
                                            {Number(period.releaseAmount).toLocaleString()}
                                        </td>
                                        <td className="py-2 pr-4 tabular-nums">
                                            {used.toLocaleString()}
                                        </td>
                                        <td className="py-2 tabular-nums">
                                            {unusedCreditsColumn.value(period).toLocaleString()}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </DialogFrame>
        </>
    );
}

function MetaItem({ label, value, hint }: { label: string; value: string; hint?: string }) {
    return (
        <div>
            <p className="text-sm text-neutral-500">{label}</p>
            <p className="mt-1 font-medium capitalize text-neutral-950">{value}</p>
            {hint ? <p className="mt-1 text-xs text-neutral-500">{hint}</p> : null}
        </div>
    );
}
